/**
 * Trajectoire officielle (PLF / PSMT) pour comparaison (ADR 0011, red team v0.2 B4 / §5).
 * Source : `baseline.outlook` (contrat v0.3 supposé, détecté à l'exécution, formes tolérées ci-dessous) ; à défaut, les
 * prévisions déjà présentes dans la baseline (`apu.<kpi>_forecast…` portant une année) — chiffres sourcés, jamais en dur.
 *
 * Formes tolérées pour `outlook` :
 *   { label?, vintage?, source?|sources?, deficit_pct_gdp: Série, debt_pct_gdp: Série, … }  avec Série =
 *     Figure[] (champ `year`) | { "2027": Figure | nombre, … } | { years: number[], values: number[], low?: number[], high?: number[] }
 *   { years: number[], deficit_pct_gdp: number[], debt_pct_gdp: number[], deficit_pct_gdp_low?: number[], … }
 *   [{ year, deficit_pct_gdp, debt_pct_gdp, … }]
 * Le solde est ramené à la convention des KPIs (négatif = déficit) en se calant sur le signe de `apu.deficit_pct_gdp`.
 */
import type { Baseline, Source } from "../../engine/src/types.js";

export interface OutlookPoint {
  year: number;
  value: number;
  low?: number;
  high?: number;
}

export type OutlookKey = "deficit_pct_gdp" | "debt_pct_gdp";

export interface Outlook {
  /** « trajectoire du gouvernement (PLF 2027) ». */
  label: string;
  series: Partial<Record<OutlookKey, OutlookPoint[]>>;
  sources: Source[];
  /** true = reconstruite à partir des prévisions ponctuelles de la baseline (pas de `baseline.outlook`). */
  derived: boolean;
}

const KEYS: Record<OutlookKey, string[]> = {
  // `apu_*` : contrat du constructeur de données v0.3 (baseline.outlook, ADR 0010 §1) ; sans lui, l'UI retombait sur 2 années.
  deficit_pct_gdp: ["apu_deficit_pct_gdp", "deficit_pct_gdp", "balance_pct_gdp", "solde_pct_gdp", "deficit"],
  debt_pct_gdp: ["apu_debt_pct_gdp", "debt_pct_gdp", "dette_pct_gdp", "debt"],
};

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const fin = (x: unknown): number | undefined => (typeof x === "number" && Number.isFinite(x) ? x : undefined);
const isSource = (s: unknown): s is Source => isObj(s) && typeof s.url === "string" && typeof s.title === "string";

function point(year: number, v: unknown): OutlookPoint | null {
  if (!Number.isInteger(year)) return null;
  if (fin(v) !== undefined) return { year, value: v as number };
  if (!isObj(v)) return null;
  const value = fin(v.value);
  if (value === undefined) return null;
  const lo = fin(v.low);
  const hi = fin(v.high);
  return { year, value, ...(lo !== undefined ? { low: lo } : {}), ...(hi !== undefined ? { high: hi } : {}) };
}

function collectSources(x: unknown, into: Source[]) {
  if (isSource(x) && !into.some((s) => s.url === x.url)) into.push(x);
  else if (Array.isArray(x)) for (const s of x) collectSources(s, into);
  else if (isObj(x) && "source" in x) collectSources(x.source, into);
}

function parseSeries(raw: unknown, years: number[] | undefined, lowArr: unknown, highArr: unknown, sources: Source[]): OutlookPoint[] {
  const out: OutlookPoint[] = [];
  if (Array.isArray(raw)) {
    raw.forEach((v, i) => {
      const y = isObj(v) && fin(v.year) !== undefined ? (v.year as number) : years?.[i];
      if (y === undefined) return;
      const p = point(y, v);
      if (!p) return;
      const lo = Array.isArray(lowArr) ? fin(lowArr[i]) : undefined;
      const hi = Array.isArray(highArr) ? fin(highArr[i]) : undefined;
      if (lo !== undefined) p.low = lo;
      if (hi !== undefined) p.high = hi;
      out.push(p);
      collectSources(v, sources);
    });
  } else if (isObj(raw)) {
    if (Array.isArray(raw.years) && Array.isArray(raw.values)) return parseSeries(raw.values, raw.years as number[], raw.low, raw.high, (collectSources(raw, sources), sources));
    for (const [k, v] of Object.entries(raw)) {
      if (!/^\d{4}$/.test(k)) continue;
      const p = point(Number(k), v);
      if (p) out.push(p);
      collectSources(v, sources);
    }
  }
  return out.sort((a, b) => a.year - b.year);
}

function vintageOf(o: Record<string, unknown>, sources: Source[]): string | undefined {
  if (typeof o.vintage === "string" && o.vintage.trim()) return o.vintage.trim();
  for (const s of sources) {
    const m = /\b(PLF(?:SS)?|PSMT|PSTAB|RESF|LPFP)\b[^,;()]*?\b(20\d\d)\b/.exec(`${s.title} ${s.publisher}`);
    if (m) return `${m[1]} ${m[2]}`;
  }
  return undefined;
}

/** Aligne le signe du solde sur celui de la baseline (KPI `deficit_pct_gdp` = solde, négatif = déficit). */
function alignBalanceSign(points: OutlookPoint[], b: Baseline): OutlookPoint[] {
  const ref = b.apu.deficit_pct_gdp?.value;
  if (ref === null || ref === undefined || !points.length) return points;
  const pos = points.filter((p) => p.value > 0).length;
  const wrong = ref < 0 ? pos > points.length / 2 : pos < points.length / 2;
  return points.map((p) => orderBand(wrong ? { year: p.year, value: -p.value, ...(p.high !== undefined ? { low: -p.high } : {}), ...(p.low !== undefined ? { high: -p.low } : {}) } : p));
}

/** Fourchette rangée (low ≤ high) quelle que soit la convention de la source. */
function orderBand(p: OutlookPoint): OutlookPoint {
  if (p.low === undefined || p.high === undefined || p.low <= p.high) return p;
  return { ...p, low: p.high, high: p.low };
}

function fromOutlook(raw: unknown, b: Baseline): Outlook | null {
  const sources: Source[] = [];
  const series: Outlook["series"] = {};
  let meta: Record<string, unknown> = {};
  if (Array.isArray(raw)) {
    for (const key of Object.keys(KEYS) as OutlookKey[]) {
      const pts: OutlookPoint[] = [];
      for (const row of raw) {
        if (!isObj(row) || fin(row.year) === undefined) continue;
        for (const alias of KEYS[key]) {
          const p = point(row.year as number, row[alias]);
          if (p) {
            const lo = fin(row[`${alias}_low`]);
            const hi = fin(row[`${alias}_high`]);
            if (lo !== undefined) p.low = lo;
            if (hi !== undefined) p.high = hi;
            pts.push(p);
            break;
          }
        }
        collectSources(row, sources);
      }
      if (pts.length) series[key] = pts.sort((a, c) => a.year - c.year);
    }
  } else if (isObj(raw)) {
    meta = raw;
    collectSources(raw.source, sources);
    collectSources(raw.sources, sources);
    const years = Array.isArray(raw.years) ? (raw.years as number[]) : undefined;
    for (const key of Object.keys(KEYS) as OutlookKey[]) {
      const alias = KEYS[key].find((a) => raw[a] !== undefined);
      if (!alias) continue;
      const pts = parseSeries(raw[alias], years, raw[`${alias}_low`], raw[`${alias}_high`], sources);
      if (pts.length) series[key] = pts;
    }
  }
  if (series.deficit_pct_gdp) series.deficit_pct_gdp = alignBalanceSign(series.deficit_pct_gdp, b);
  if (series.debt_pct_gdp) series.debt_pct_gdp = series.debt_pct_gdp.map(orderBand);
  if (!Object.keys(series).length) return null;
  const v = vintageOf(meta, sources);
  const label = typeof meta.label === "string" && meta.label.trim() ? meta.label.trim() : `trajectoire du gouvernement${v ? ` (${v})` : ""}`;
  return { label, series, sources, derived: false };
}

/** Repli : prévisions ponctuelles de la baseline (`apu.<kpi>_forecast`, `apu.<kpi>_forecast_<année>`), sourcées. */
function fromForecasts(b: Baseline): Outlook | null {
  const sources: Source[] = [];
  const series: Outlook["series"] = {};
  for (const key of Object.keys(KEYS) as OutlookKey[]) {
    const pts = new Map<number, OutlookPoint>();
    for (const [k, f] of Object.entries(b.apu)) {
      if (!k.startsWith(`${key}_forecast`) || !f || f.value === null || f.unit !== "pct_gdp") continue;
      const m = /_forecast(?:_(\d{4}))?$/.exec(k);
      const year = m?.[1] ? Number(m[1]) : f.year;
      if (!m || year === undefined || pts.has(year)) continue;
      pts.set(year, { year, value: f.value, ...(f.low !== undefined ? { low: f.low } : {}), ...(f.high !== undefined ? { high: f.high } : {}) });
      collectSources(f.source, sources);
    }
    if (pts.size) series[key] = [...pts.values()].sort((a, c) => a.year - c.year);
  }
  if (series.deficit_pct_gdp) series.deficit_pct_gdp = alignBalanceSign(series.deficit_pct_gdp, b);
  if (!Object.keys(series).length) return null;
  const v = vintageOf({}, sources);
  return { label: `trajectoire du gouvernement${v ? ` (${v})` : ""}`, series, sources, derived: true };
}

export function officialOutlook(b: Baseline): Outlook | null {
  const raw = (b as unknown as { outlook?: unknown }).outlook;
  return (raw !== undefined ? fromOutlook(raw, b) : null) ?? fromForecasts(b);
}
